import { getSparkflowGlobalContext, setSparkflowGlobalContext } from '@thoughtspot/blink-context';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { jsonConstants } from '@thoughtspot/json-constants';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import { Horizontal, Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { getSessionInfo, setSessionInfo, UserPersona, } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useBlinkContextForSparkflow } from "../contexts/sparkflow-blink-context";
import { setSparkflowConfig } from "../contexts/sparkflow-context";
import { useUpdateUserPersona } from "../services/user-admin/user-admin-service";
import { intentOptionSelected, intentOptionsOpened, intentPageErrorShown, intentPageLoaded, intentSelectionConfirmed, PageContext, } from "../utils/metrics/metrics-utils";
import { FUNCTION_TO_PERSONA_MAP, getFunctionOptions, getIndustryOptions, ONBOARDING_IMAGE_SRC, } from "../utils/sparkflow-constants";
import { ONBOARDING_KEY } from "../utils/types";
import { getSavedOnboardingSelections } from "../utils/utils";
import { QuestionForm } from './question-form/question-form';
import styles from './sparkflow-container.module.scss';
import { AppLogo } from './spotbuild-integration/components/app-logo/app-logo.container';
export const SparkflowContainer = () => {
    const { metricsService, navService, sessionService } = useBlinkContextForSparkflow();
    const sparkflowDispatchers = setSparkflowConfig();
    const { updateUserPersona } = useUpdateUserPersona();
    const [bannerAlertMessage, setBannerAlertMessage] = useState({
        isVisible: false,
        errorMessage: '',
    });
    const onboardingMTServicesEnabled = sessionService.isOnboardingMTServicesEnabled();
    const { t } = useTranslation();
    const { savedRole, isGeneratedIdsLoading } = getSparkflowGlobalContext();
    const { updateSparkflowGlobalContext, } = setSparkflowGlobalContext();
    const functionOptions = useMemo(() => getFunctionOptions(), []);
    const industryOptions = useMemo(() => getIndustryOptions(), []);
    const savedOnboardingSelections = useMemo(() => getSavedOnboardingSelections(functionOptions, industryOptions, onboardingMTServicesEnabled, savedRole), [functionOptions, industryOptions, savedRole]);
    const [formData, setFormData] = useState(savedOnboardingSelections);
    const getFunctionLabel = useCallback((id) => functionOptions.find(opt => opt.id === id)?.label || '', [functionOptions]);
    const getIndustryLabel = useCallback((id) => industryOptions.find(opt => opt.id === id)?.label || '', [industryOptions]);
    useEffect(() => {
        intentPageLoaded(metricsService, {
            pageContext: PageContext.INTENT_PAGE,
            preFilledFunction: getFunctionLabel(formData.function),
            preFilledIndustry: getIndustryLabel(formData.industry),
        });
    }, [metricsService]);
    useEffect(() => {
        setFormData(prev => !prev.function && !prev.industry ? savedOnboardingSelections : prev);
    }, [savedOnboardingSelections]);
    const handleSelectChange = (option, field) => {
        setFormData(prev => ({ ...prev, [field]: option.id }));
        let updatedFunction = formData.function;
        let updatedIndustry = formData.industry;
        if (field === ONBOARDING_KEY.FUNCTION) {
            updatedFunction = option.id;
        }
        else {
            updatedIndustry = option.id;
        }
        intentOptionSelected(metricsService, {
            pageContext: PageContext.INTENT_PAGE,
            dropDown: field,
            dropDownSelection: option.id,
            preFilledFunction: getFunctionLabel(field === ONBOARDING_KEY.FUNCTION
                ? formData.function
                : updatedFunction),
            preFilledIndustry: getIndustryLabel(field === ONBOARDING_KEY.INDUSTRY
                ? formData.industry
                : updatedIndustry),
        });
    };
    const handleDropdownOpen = (dropdownType) => (isOpen) => {
        if (isOpen) {
            intentOptionsOpened(metricsService, {
                pageContext: PageContext.INTENT_PAGE,
                dropDown: dropdownType,
                preFilledFunction: getFunctionLabel(formData.function),
                preFilledIndustry: getIndustryLabel(formData.industry),
            });
        }
    };
    const saveUserProperties = async () => {
        updateSparkflowGlobalContext({
            savedRole: {
                function: formData.function,
                industry: formData.industry,
            },
        });
        const persona = FUNCTION_TO_PERSONA_MAP[formData.function] ||
            UserPersona.BUSINESS_PERSONA;
        await updateUserPersona(persona);
        const existingSessionInfo = getSessionInfo();
        const newUserPersona = jsonConstants.userPersona[persona] || jsonConstants.userPersona.business_persona;
        const updatedSessionInfo = {
            ...existingSessionInfo,
            userProperties: {
                ...existingSessionInfo.userProperties,
                persona: newUserPersona,
                function: formData.function,
                industry: formData.industry,
            },
        };
        setSessionInfo(updatedSessionInfo);
    };
    const handleGetStarted = useCallback(async () => {
        sparkflowDispatchers?.updateOnboardingSelections?.({
            selectedFunction: formData.function || '',
            selectedIndustry: formData.industry || '',
        });
        try {
            await saveUserProperties();
            navService.goToSpotbuildIntegration();
            intentSelectionConfirmed(metricsService, {
                pageContext: PageContext.INTENT_PAGE,
                selectedFunction: formData.function || '',
                selectedIndustry: formData.industry || '',
                preFilledFunction: getFunctionLabel(formData.function),
                preFilledIndustry: getIndustryLabel(formData.industry),
            });
        }
        catch (error) {
            setBannerAlertMessage({
                isVisible: true,
                errorMessage: error instanceof Error ? error.message : t('error.validation.default'),
            });
            intentPageErrorShown(metricsService, {
                pageContext: PageContext.INTENT_PAGE,
                selectedFunction: formData.function || '',
                selectedIndustry: formData.industry || '',
                preFilledFunction: getFunctionLabel(formData.function),
                preFilledIndustry: getIndustryLabel(formData.industry),
                errorType: error instanceof Error ? error.message : 'Form submission error',
            });
        }
    }, [formData, metricsService, getFunctionLabel, getIndustryLabel]);
    const isFormComplete = useMemo(() => formData.function.trim() && formData.industry, [formData]);
    return (React.createElement(React.Fragment, null,
        React.createElement(GlobalAlert.Banner, { type: BannerAlertType.ERROR, "data-testid": "sparkflow-onboarding-banner-alert", message: bannerAlertMessage.errorMessage, isVisible: bannerAlertMessage.isVisible, onClose: () => setBannerAlertMessage({ isVisible: false, errorMessage: '' }) }),
        React.createElement(Horizontal, { className: styles.sparkflowContainer, grow: true },
            React.createElement(Vertical, { className: styles.formSection, grow: true, basis: "60%" },
                React.createElement(AppLogo, null),
                React.createElement(QuestionForm, { formData: formData, functionOptions: functionOptions, industryOptions: industryOptions, isFormComplete: !!isFormComplete, handleSelectChange: handleSelectChange, handleDropdownOpen: handleDropdownOpen, handleGetStarted: handleGetStarted, isGeneratedIdsLoading: !!isGeneratedIdsLoading && onboardingMTServicesEnabled })),
            React.createElement(Vertical, { className: styles.imageSection, grow: true, basis: "40%", vAlignContent: "center", hAlignContent: "center" },
                React.createElement("div", { className: styles.onboardingImageWrapper },
                    React.createElement("img", { src: ONBOARDING_IMAGE_SRC, alt: "Sparkflow onboarding preview", className: styles.onboardingImage }))))));
};
//# sourceMappingURL=sparkflow-container.js.map